import { observer } from "mobx-react-lite";
import { MdsIconKind } from "@/design-system/components/icon";
import { css } from "@/domains/emotion";
import { MdsChipInput } from "@/design-system/components/input";
import { CollectionChip } from "@/components/chips/collection-chip";
import { noop } from "lodash-es";
import { UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { UNTITLED_TEMPLATE_TITLE } from "@/domains/untitled/untitled";
import { ShareSheetPermissionRow } from "@/components/modal/share-sheet/share-sheet-permission-row/share-sheet-permission-row";
import { ShareSheetModalProps } from "@/components/modal/share-sheet/share-sheet-modal";
import { useLastNonNullRender } from "@/hooks/useLastNonNullRender";
import { ShareSheetEntityKind } from "@/components/modal/share-sheet/types";
import { FadeVerticalScroll } from "@/components/vertical-scroll/FadeVerticalScroll";
import { TemplateChip } from "@/components/chips/template-chip";
import { NoteChip } from "@/components/chips/note-chip";
import {
  MDSButton,
  MDSButtonSlotName,
  MDSButtonIcon,
  MDSButtonVariant,
  MDSText,
  MDSTextVariant,
  MDSDialog,
  MDSDialogHeaderContent,
  MDSDialogSlotName,
} from "@/mds";
import styled from "@emotion/styled";

export const ShareSheetModalContent = observer<ShareSheetModalProps>(
  function ShareSheetModalContent({ store: { modal: store } }) {
    return useLastNonNullRender(() => {
      if (!store) return null;

      const titleChip = (() => {
        if (store.entityKind === ShareSheetEntityKind.Collection && store.collection) {
          return <CollectionChip id={store.collection.id} label={store.collection.label} />;
        }
        if (store.entityKind === ShareSheetEntityKind.Note && store.note) {
          return <NoteChip title={store.note.title || UNTITLED_NOTE_TITLE} />;
        }
        if (store.entityKind === ShareSheetEntityKind.Template && store.template) {
          return <TemplateChip title={store.template.title || UNTITLED_TEMPLATE_TITLE} />;
        }
      })();
      if (!titleChip) return null;

      const isShareDisabled = !store.chips.length || store.isSharing;
      return (
        <MDSDialog isOpen handleClose={() => store.close()}>
          <MDSDialogHeaderContent slot={MDSDialogSlotName.Header} allowOverflow={false}>
            <MDSText variant={MDSTextVariant.HeaderL}>Share</MDSText>
            {titleChip}
          </MDSDialogHeaderContent>

          <StyledModalContent
            slot={MDSDialogSlotName.Content}
            onKeyDownCapture={e => {
              if (e.key === "Escape") store.close();
            }}
          >
            <div className={rowStyles}>
              <MdsChipInput
                autoFocus
                isDisabled={store.isSharing}
                getChips={store.getChips}
                chips={store.chips}
                setChips={store.setChips}
                onSubmit={isShareDisabled ? noop : store.handleSubmit}
                placeholder={
                  store.entityKind === ShareSheetEntityKind.Collection
                    ? "Share with people or emails"
                    : "Share with people, emails, or collections"
                }
                chipIconSize={16}
              />
              <StyledShareButton
                variant={
                  !store.isSharing ? MDSButtonVariant.Emphasis : MDSButtonVariant.Transparent
                }
                isDisabled={isShareDisabled}
                isLoading={store.isSharing}
                onClick={store.handleSubmit}
              >
                Share
              </StyledShareButton>
            </div>

            <FadeVerticalScroll className={scrollableSectionStyles}>
              <div className={aclEntryStyles}>
                {store.aclEntries.map((permission, i) => (
                  <ShareSheetPermissionRow
                    key={i}
                    isUnsharing={store.isUnsharing}
                    permission={permission}
                    isGuestMode={store.isGuestMode}
                    canRevokeAccess={store.canRevokeAccess}
                    canUpdateAccess={store.canUpdateAccess}
                    handleRevokeAccess={store.handleRevokeAccess}
                    handleUpdateAccess={store.handleUpdateAccess}
                    handleCancelOperation={store.handleCancelOperation}
                    shareSheetModalStore={store}
                  />
                ))}
              </div>
            </FadeVerticalScroll>
          </StyledModalContent>

          <StyledModalFooter slot={MDSDialogSlotName.Footer}>
            <StyledCopyLinkButton
              variant={MDSButtonVariant.Transparent}
              onClick={store.handleCopyCollaborativeLink}
            >
              <MDSButtonIcon slot={MDSButtonSlotName.LeftIcon} kind={MdsIconKind.LinkSolid} />
              Copy collaborative link
            </StyledCopyLinkButton>
            <StyledSmallText variant={MDSTextVariant.TextS}>
              Only people added above can use this link
            </StyledSmallText>
          </StyledModalFooter>
        </MDSDialog>
      );
    });
  }
);

const StyledModalContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 8px;
  gap: ${({ theme }) => theme.spacing.sm};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`;

const StyledModalFooter = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  padding-right: ${({ theme }) => theme.spacing[2]};
  padding-top: ${({ theme }) => theme.spacing[2]};
  @media (max-width: 768px) {
    padding: 16px 24px 24px 24px;
    flex-direction: column;
    gap: 4px;
  }
`;

const rowStyles = css({
  width: "100%",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
});

const aclEntryStyles = css({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
});

const scrollableSectionStyles = css({
  maxHeight: "max(120px, calc(100vh - 300px))",
});

const StyledSmallText = styled(MDSText)`
  text-align: center;
`;

const StyledShareButton = styled(MDSButton)`
  height: 40px;
`;

const StyledCopyLinkButton = styled(MDSButton)`
  color: ${({ theme }) => theme.colors.text.emphasis};
  & .mds-icon {
    color: ${({ theme }) => theme.colors.text.emphasis};
  }
`;
